import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Save, UserRound } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, useValidatedForm, isBlank, isEmail, isMobile } from "@/components/app/FormKit";
import { btnPrimary, cardCls, fmtDate, pageSub, pageTitle } from "@/components/app/ui";

export default function AccountProfile({ title = "My Profile", subtitle, testId = "account-profile" }) {
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [changing, setChanging] = useState(false);

  const validateProfile = useCallback((f) => {
    const e = {};
    if (isBlank(f.name)) e.name = "Name is required.";
    else if (f.name.trim().length < 2) e.name = "Enter your full name.";
    if (isBlank(f.email)) e.email = "Email is required.";
    else if (!isEmail(f.email)) e.email = "Enter a valid email address.";
    if (!isBlank(f.mobile) && !isMobile(f.mobile)) e.mobile = "Enter a valid 10-digit mobile number.";
    return e;
  }, []);
  const profile = useValidatedForm({ name: "", email: "", mobile: "" }, validateProfile);

  const validatePassword = useCallback((f) => {
    const e = {};
    if (isBlank(f.current_password)) e.current_password = "Enter your current password.";
    if (isBlank(f.new_password)) e.new_password = "Enter a new password.";
    else if (f.new_password.length < 8) e.new_password = "Use at least 8 characters.";
    else if (f.new_password === f.current_password) e.new_password = "Choose a password different from the current one.";
    if (f.confirm !== f.new_password) e.confirm = "The passwords don't match.";
    return e;
  }, []);
  const password = useValidatedForm({ current_password: "", new_password: "", confirm: "" }, validatePassword);

  useEffect(() => {
    api.get("/auth/profile").then(({ data }) => profile.reset({ name: data.name || "", email: data.email || "", mobile: data.mobile || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProfile = async () => {
    if (!profile.check()) return;
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name: profile.values.name.trim(), email: profile.values.email.trim(), mobile: profile.values.mobile.replace(/[\s-]/g, "") });
      if (data.token) localStorage.setItem("s24_token", data.token);          // the token carries the email
      setUser((u) => ({ ...u, name: data.name, email: data.email, mobile: data.mobile }));
      toast.success("Profile updated.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (!password.check()) return;
    setChanging(true);
    try {
      await api.put("/auth/password", { current_password: password.values.current_password, new_password: password.values.new_password });
      password.reset({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed. Use the new password the next time you sign in.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setChanging(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid={testId}>
      <div>
        <h1 className={pageTitle}>{title}</h1>
        <p className={pageSub}>{subtitle}</p>
      </div>

      <section className={`${cardCls} p-5`}>
        <div className="flex items-center gap-2 mb-3"><UserRound size={16} className="text-blue-600" /><h2 className="text-[14px] font-bold">Account details</h2>
          {user?.created_at && <span className="ml-auto text-[11px] text-slate-500">Member since {fmtDate(user.created_at)}</span>}</div>
        <div className="grid grid-cols-6 gap-x-3 gap-y-3">
          <Field className="col-span-6 sm:col-span-3" label="Full name" required t="profile-name" autoComplete="name" {...profile.bind("name")} />
          <Field className="col-span-6 sm:col-span-3" label="Email" required type="email" t="profile-email" autoComplete="email" hint="You sign in with this email." {...profile.bind("email")} />
          <Field className="col-span-6 sm:col-span-3" label="Mobile" type="tel" maxLength={10} t="profile-mobile" placeholder="10-digit number" {...profile.bind("mobile")} />
        </div>
        <div className="flex justify-end mt-4"><button onClick={saveProfile} disabled={saving} data-testid="profile-save" className={btnPrimary}><Save size={14} /> {saving ? "Saving…" : "Save profile"}</button></div>
      </section>

      <section className={`${cardCls} p-5`}>
        <div className="flex items-center gap-2 mb-3"><KeyRound size={16} className="text-orange-600" /><h2 className="text-[14px] font-bold">Change password</h2></div>
        <div className="grid grid-cols-6 gap-x-3 gap-y-3">
          <Field className="col-span-6 sm:col-span-2" label="Current password" required type="password" t="password-current" autoComplete="current-password" {...password.bind("current_password")} />
          <Field className="col-span-6 sm:col-span-2" label="New password" required type="password" t="password-new" autoComplete="new-password" hint="At least 8 characters." {...password.bind("new_password")} />
          <Field className="col-span-6 sm:col-span-2" label="Confirm new password" required type="password" t="password-confirm" autoComplete="new-password" {...password.bind("confirm")} />
        </div>
        <div className="flex justify-end mt-4"><button onClick={changePassword} disabled={changing} data-testid="password-save" className={btnPrimary}>{changing ? "Updating…" : "Update password"}</button></div>
      </section>
    </div>
  );
}
